(() => {
  const active = new WeakMap();
  const pageProfiles = {
    index: 'homepage',
    collection: 'collection',
    list_collections: 'collection-index',
    product: 'product',
    search: 'search',
    cart: 'cart',
    blog: 'blog',
    article: 'article',
    page: 'content',
    customers: 'customer',
  };
  const imageSelector = [
    '.card__media img',
    '.product__media img',
    '.collection-hero__image-container img',
    '.image-with-text__media img',
    'product-recommendations img',
    '[data-predictive-search] img',
    '.cart-item__image',
  ].join(',');

  const block = (className = '') =>
    `<span class="cc-skeleton-block ${className}" aria-hidden="true"></span>`;

  const card = () => `
    <div class="cc-skeleton-card" aria-hidden="true">
      ${block('cc-skeleton-card__image')}
      ${block('cc-skeleton-card__title')}
    </div>`;

  const grid = (count = 4) => `
    <div class="cc-skeleton-grid" aria-hidden="true">
      ${Array.from({ length: count }, card).join('')}
    </div>`;

  const row = (kind) => `
    <div class="cc-skeleton-${kind}-row" aria-hidden="true">
      ${block(`cc-skeleton-${kind}-row__image`)}
      <span class="cc-skeleton-${kind}-row__copy">
        ${block('cc-skeleton-block--heading')}
        ${block('cc-skeleton-block--text cc-skeleton-block--text-short')}
      </span>
    </div>`;

  const templates = {
    'product-grid': () => grid(4),
    recommendations: () => `${block('cc-skeleton-block--heading')}${grid(4)}`,
    predictive: () => Array.from({ length: 4 }, () => row('predictive')).join(''),
    'cart-row': () => row('cart'),
    'cart-summary': () =>
      `${block('cc-skeleton-block--heading')}${block('cc-skeleton-block--text')}${block('cc-skeleton-block--button')}`,
    banner: () => block('cc-skeleton-block--banner'),
    content: () =>
      `${block('cc-skeleton-block--heading')}${block('cc-skeleton-block--text')}${block('cc-skeleton-block--text cc-skeleton-block--text-short')}`,
  };

  const resolveTarget = (target) =>
    typeof target === 'string' ? document.querySelector(target) : target;

  const end = (target) => {
    const element = resolveTarget(target);
    if (!element) return;
    const state = active.get(element);
    if (state?.timeout) window.clearTimeout(state.timeout);
    state?.overlay?.remove();
    active.delete(element);
    element.classList.remove('cc-skeleton-host');
    element.removeAttribute('aria-busy');
    delete element.dataset.ccSkeletonKind;
  };

  const start = (target, kind = 'content', timeout = 12000) => {
    const element = resolveTarget(target);
    if (!element || active.has(element)) return;

    const render = templates[kind] || templates.content;
    const overlay = document.createElement('div');
    overlay.className = `cc-skeleton-overlay cc-skeleton-overlay--${kind}`;
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = render();

    element.classList.add('cc-skeleton-host');
    element.setAttribute('aria-busy', 'true');
    element.dataset.ccSkeletonKind = kind;
    element.appendChild(overlay);

    const timeoutId = window.setTimeout(() => end(element), timeout);
    active.set(element, { overlay, timeout: timeoutId });
  };

  const imageParent = (image) => image.closest('.media') || image.parentElement;

  const bindImage = (image) => {
    if (!image || image.dataset.ccSkeletonBound === 'true') return;
    image.dataset.ccSkeletonBound = 'true';
    const parent = imageParent(image);
    if (!parent) return;

    const clear = () => parent.classList.remove('cc-skeleton-image-pending');
    if (image.complete) {
      clear();
      return;
    }

    parent.classList.add('cc-skeleton-image-pending');
    image.addEventListener('load', clear, { once: true });
    image.addEventListener('error', clear, { once: true });
    window.setTimeout(clear, 12000);
  };

  const bindImages = (scope = document) => {
    if (scope.matches?.(imageSelector)) bindImage(scope);
    scope.querySelectorAll?.(imageSelector).forEach(bindImage);
  };

  document.addEventListener('cc:loading:start', (event) => {
    start(event.detail?.target, event.detail?.kind, event.detail?.timeout);
  });
  document.addEventListener('cc:loading:end', (event) => end(event.detail?.target));

  const initialise = () => {
    const pageType = document.body.dataset.ccPageType || document.body.dataset.ccTemplateName;
    document.body.dataset.ccSkeletonProfile = pageProfiles[pageType] || 'content';
    bindImages();
    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) bindImages(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }

  window.CCSkeleton = { start, end, bindImages };
})();
